import { createTheme } from "@mui/material";
import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { darkThemeOptions, lightThemeOptions } from "../themeOptions";

export type ThemeMode = "light" | "dark";
export type Language = "en" | "es-MX";

// Language deliberately does NOT live here. It is stored per user on the server
// (User.Language, falling back to Account.Language) so it follows the user across
// devices and so outbound email can be written in the same language the user reads.
// See useLanguagePreference. On unauthenticated pages the language is whatever
// i18next's own detector resolves, since there is no user to read a setting from.
type PreferencesState = {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;

  // hydration flag to avoid theme flicker on first paint
  hydrated: boolean;
  setHydrated: (v: boolean) => void;
};

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      theme: "light",
      setTheme: (mode) => set({ theme: mode }),
      hydrated: false,
      setHydrated: (v) => set({ hydrated: v }),
    }),
    {
      name: "cf_prefs",
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        // mark as hydrated after rehydrate finishes
        state?.setHydrated(true);
      },
    }
  )
);



/**
 * Hide the casting needed to reach zustand-persist's rehydrate.
 * Returns the `rehydrate` function or undefined if not present.
 */
export function userPreferencesRehydrate(): (() => void) | undefined {
  type MaybePersist = { persist?: { rehydrate: () => void } };
  const store = usePreferencesStore as unknown as MaybePersist;
  return store.persist?.rehydrate;
}

/**
 * Custom hook used by App to get a ready MUI theme and hydration status.
 * It triggers rehydration ASAP and memoizes the theme object.
 */
export function useUserTheme() {
  const themeMode = usePreferencesStore((s) => s.theme);
  const hydrated = usePreferencesStore((s) => s.hydrated);

  const rehydrate = userPreferencesRehydrate();
  useEffect(() => {
    rehydrate?.();
  }, [rehydrate]);

  const theme = useMemo(
    () => createTheme(themeMode === "dark" ? darkThemeOptions : lightThemeOptions),
    [themeMode]
  );

  return { theme, hydrated, themeMode };
}